import { IconButton, Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { missingChannelEpisodeFilters } from "@/components/dataset/episodeReviewState";
import { hatcheryTextModes } from "@/app/theme";
import type { FolderScanResult, UploadEngine, UploadJobView } from "@/upload";
import { formatUploadBytes } from "@/page/datasetUpload/datasetUploadFormatters";
import {
  formatJobStatus,
  missingChannelExcluded,
  progressPercent,
  retryButtonText,
  showProgress,
  jobTone,
} from "./uploadJobStatus";
import styles from "./uploadWindow.module.css";

type UploadStatusBarProps = {
  // 진행 중인 잡. 업로드 시작 전(설정 단계)에는 null 이다. 그때는 스캔 요약을 그린다.
  job: UploadJobView | null;
  // 설정 단계 요약에 쓰는 스캔 결과. 폴더를 선택하지 않았으면 null.
  scan: FolderScanResult | null;
  engine: UploadEngine;
  // 완료나 폐기로 창을 닫는다. 상위(UploadWindowContent)가 엔진 잡 dismiss 와 창 close 를 소유한다.
  onClose: () => void;
  // 우측 목록에서 집계한 완료·실패·전체 수. 요약 카운터와 문구에 쓴다.
  counts: { done: number; failed: number; total: number };
};

/**
 * 업로드 창 하단 상태 바. 우측 파일 목록 바로 아래에서 "이 업로드가 지금 어떤 상태인가" 를
 * 표시한다. 설정 단계에는 스캔 요약을 그린다. 시작 뒤에는 잡 상태, 진행 바, 완료·실패 카운트,
 * 전체 액션을 그린다.
 *
 * 이 컴포넌트는 문구 컬럼과 액션 컬럼을 나누려고 있다. 좁은 컬럼에서 완료 문구가 여러 줄로
 * 줄바꿈되어도 버튼은 자기 컬럼에 남는다. 그래서 `닫기` 가 스크롤 경계 밖으로 밀리지 않는다.
 *
 * finalize(완료 처리)는 별도 줄을 만들지 않는다. 잡 상태 문구가 이미 같은 단계를 표시한다.
 * `completing` 문구가 finalize running 에 해당한다. finalize 가 실패하면 잡은 failed 가 된다.
 * 목록에서 뺀 finalize 재시도만 이 바의 액션이 맡는다.
 *
 * 앞으로 업로드 전 사전 검사(ZERG-435 §B)의 소견도 여기에 표시한다. 그때 새 어휘를 만들지 않고
 * `DatasetInspectWarning`(`@/api`, `{code, severity, detail}`)을 쓴다. 리뷰 탭 MetadataCard 가
 * 커밋 **후** 소견을 이미 그 어휘로 그린다. 그래서 커밋 전과 후의 표시가 같은 형태여야 한다.
 *
 * Owns: 하단 바의 표시와 전체 액션 배치.
 * Does not own: 상태 문구·진행률·tone 계산(uploadJobStatus), 파일 단위 상태(uploadItems).
 */
export function UploadStatusBar({
  job,
  scan,
  engine,
  onClose,
  counts,
}: UploadStatusBarProps): ReactNode {
  return (
    <section className={styles.statusBar} aria-label="업로드 상태">
      <div className={styles.statusMain}>
        {job === null ? <ScanSummary scan={scan} /> : <JobSummary job={job} counts={counts} />}
      </div>
      {job === null ? null : (
        <div className={styles.actions}>{renderActions(job, engine, onClose)}</div>
      )}
    </section>
  );
}

// 설정 단계. 무엇을 업로드하려는지만 표시한다. 시작과 취소는 폼이 소유한다.
function ScanSummary({ scan }: { scan: FolderScanResult | null }): ReactNode {
  if (scan === null || scan.files.length === 0) {
    return (
      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.meta}>
        업로드할 폴더를 선택해 주세요.
      </Text>
    );
  }
  return (
    <Text variant={hatcheryTextModes["caption-normal"]} className={styles.meta}>
      {`파일 ${scan.files.length.toLocaleString()}개 · 총 ${formatUploadBytes(scan.totalBytes)}`}
      {scan.rejected.length > 0 ? ` · 제외 ${scan.rejected.length.toLocaleString()}개` : ""}
    </Text>
  );
}

function JobSummary({
  job,
  counts,
}: {
  job: UploadJobView;
  counts: UploadStatusBarProps["counts"];
}): ReactNode {
  return (
    <>
      <Text
        variant={hatcheryTextModes["body-bold"]}
        className={styles.statusText}
        data-tone={jobTone(job)}
      >
        {formatJobStatus(job)}
      </Text>

      {showProgress(job) ? (
        <div className={styles.progressTrack} aria-hidden="true">
          <span className={styles.progressFill} style={{ width: `${progressPercent(job)}%` }} />
        </div>
      ) : null}

      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.meta}>
        {`파일 ${counts.done.toLocaleString()}/${counts.total.toLocaleString()} 완료`}
        {counts.failed > 0 ? ` · 실패 ${counts.failed.toLocaleString()}` : ""}
      </Text>

      {job.status === "failed" &&
      (job.uploadJob?.status === "failed" || job.uploadJob?.status === "conflict") ? (
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.meta}>
          창을 닫은 뒤 업로드 현황에서 재업로드하거나 업로드 기록을 삭제할 수 있습니다.
        </Text>
      ) : null}

      {showsErrorInStatus(job) || job.errorMessage === null ? null : (
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.error} role="alert">
          {job.errorMessage}
        </Text>
      )}
    </>
  );
}

// failed·blocked 의 상태 문구는 errorMessage 를 그대로 포함한다(formatJobStatus). 한 줄 바에서
// 같은 문장을 두 번 그리지 않도록, 이 두 상태에서는 별도 에러 줄을 생략한다.
function showsErrorInStatus(job: UploadJobView): boolean {
  return job.status === "failed" || job.status === "blocked";
}

// 업로드가 채널 누락 에피소드를 학습에서 뺐으면 그 에피소드를 여는 버튼을 둔다. 사유 필터 하나로
// 에피소드 탭을 연다. 그러면 제외된 행도 보이므로 건수와 행 수가 같다.
function renderMissingChannelAction(job: UploadJobView): ReactNode {
  const dataset = job.uploadJob;
  if (missingChannelExcluded(job) === null || !dataset?.dataset_uuid) {
    return null;
  }
  const datasetUuid = dataset.dataset_uuid;
  return (
    <IconButton
      text="제외된 에피소드 보기"
      size="sm"
      variant="surface"
      tone="warning"
      onClick={() =>
        openDatasetDetailWindow({
          datasetUuid,
          displayName: dataset.display_name,
          initialTab: "episodeReview",
          episodeFilters: missingChannelEpisodeFilters(),
        })
      }
    />
  );
}

// 실패·차단 상태는 닫을 수 있다. 재시도는 복구할 수 있을 때만 표시한다. 취소는 서버의 미완료
// 잡에만 표시한다.
function renderActions(job: UploadJobView, engine: UploadEngine, onClose: () => void): ReactNode {
  switch (job.status) {
    case "queued":
    case "initializing":
    case "reconciling":
    case "uploading":
      return (
        <IconButton
          text="일시정지"
          size="sm"
          variant="surface"
          onClick={() => engine.pause(job.id)}
        />
      );
    case "paused":
      return (
        <IconButton text="재개" size="sm" variant="solid" onClick={() => engine.resume(job.id)} />
      );
    case "failed":
    case "blocked": {
      const retryText = retryButtonText(job);
      if (retryText === null) {
        return <IconButton text="닫기" size="sm" variant="surface" onClick={onClose} />;
      }
      return (
        <>
          {finalizeOnlyFailure(job) ? (
            // 전송은 끝났고 complete 호출만 실패한 경우다. complete 를 다시 호출하면 된다.
            // 목록에서 finalize 행을 뺐으므로 이 버튼이 그 재시도를 맡는다. engine.retry 는 scan 이
            // 없는 잡에서 아무것도 하지 않는다. 그래서 여기서 경우를 구분한다.
            <IconButton
              text="완료 처리 재시도"
              size="sm"
              variant="solid"
              onClick={() => engine.retryFinalize(job.id)}
            />
          ) : (
            <IconButton
              text={retryText}
              size="sm"
              variant="solid"
              onClick={() => engine.retry(job.id)}
            />
          )}
          {job.status === "failed" &&
          (job.uploadJob?.status === "pending_upload" || job.uploadJob?.status === "uploaded") ? (
            <IconButton
              text="업로드 취소"
              size="sm"
              variant="surface"
              tone="danger"
              onClick={() => engine.cancel(job.id)}
            />
          ) : null}
          <IconButton text="닫기" size="sm" variant="surface" onClick={onClose} />
        </>
      );
    }
    case "cancelled":
      return <IconButton text="닫기" size="sm" variant="surface" onClick={onClose} />;
    case "completed":
      return (
        <>
          {renderMissingChannelAction(job)}
          <IconButton text="닫기" size="sm" variant="solid" onClick={onClose} />
        </>
      );
    case "awaitingComplete":
    case "completing":
    case "processing":
      return null;
  }
}

// 마지막 실패가 finalize(complete)인지 확인한다. 파일 재시도와 전체 재시도는 finalize 를 idle 로
// 되돌린다. 그래서 이 조합은 "전송은 끝났고 complete 만 실패" 를 뜻한다. blocked(다른 브라우저가
// 점유)는 제외한다. complete 를 다시 호출해도 같은 lease 때문에 막힌다.
function finalizeOnlyFailure(job: UploadJobView): boolean {
  return job.status === "failed" && job.finalize.status === "failed";
}
